/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import get from "lodash/get";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { addTargetBlankToExtLinks } from "../../../../../utils/add-target-blank-to-extlinks.js";
import { isEmpty, selectedStoryTemplate, selectedStoryTemplateSettings } from "../../../../utils.js";
import { AdComponent, WidgetComp } from "../../../DetectComponent";
import { checkIfStoryPublic } from "../../../sso-components/sso-utils.js";
import { loadRelatedStories } from "../../../story-page-components/related-stories-helper.js";
import PaywallStory from "../../paywall-story.js";
import PublicStory from "../../public-story.js";

const StoryBase = props => {
  const {
    story,
    config,
    storyStoreField,
    storyPageType,
    preview,
    adComponent,
    widgetComp,
    storyBaseType,
    templateConfig
  } = props;
  const isStoryPublic = checkIfStoryPublic(story);
  const [relatedStories, setRelatedStories] = useState([]);
  const storyWrapperElement = useRef(null);

  useEffect(() => {
    // check if asside collection stories are provided. if yes then don't load related stories
    const asideCollectionData = get(props, ["templateConfig", "asideCollection", "data"], []);
    !asideCollectionData.length &&
      loadRelatedStories(story, config).then(relatedStories => setRelatedStories(relatedStories));
    addTargetBlankToExtLinks(storyWrapperElement.current);
  }, []);

  const pbStoryConfig = get(config, ["pagebuilder-config", storyStoreField, storyPageType], {});
  const storyTemplate = selectedStoryTemplate(storyPageType, story, config, storyStoreField);
  const showAllCards = -1;
  const { asideSlots, "story-slots": storySlots = {} } = pbStoryConfig;

  const defaultTemplateConfig = {
    theme: "",
    verticalShare: "",
    shareIconType: "plain-svg",
    templateType: storyTemplate,
    noOfVisibleCards: showAllCards,
    showSectionTag: true,
    publishedDetails: {
      enablePublishedTime: true,
      enableUpdatedTime: false,
      showReadTime: false
    },
    storySlots,
    asideCollection: {
      data: relatedStories,
      slots: asideSlots || [
        {
          type: "ad",
          layout: "Story-Mrec",
          layoutLabel: "Story-Mrec",
          config: {
            targetingId: "story-mrec-aside-collections"
          }
        },
        {
          type: "collection"
        }
      ],
      config: {
        title: "Related Stories",
        theme: get(pbStoryConfig, ["settings", "theme"], ""),
        collectionNameBorderColor: "#000",
        showAuthor: true,
        showTime: true,
        showReadTime: false,
        mountAtPrefix: get(config, ["mountAt"], "")
      }
    },
    authorDetails: { template: "default", opts: { hideBio: true, hideImage: false } }
  };

  const storyTemplateSettings = selectedStoryTemplateSettings(storyPageType, config, storyStoreField);
  const updatedConfig = { ...defaultTemplateConfig, ...templateConfig, ...storyTemplateSettings };
  const storyConfig = !isEmpty(pbStoryConfig) ? updatedConfig : defaultTemplateConfig;
  const bgColor = updatedConfig.theme || "";
  const storyElementsConfig = get(config, ["pagebuilder-config", "story", "globalStoryElements"], {});

  return (
    <div style={{ backgroundColor: bgColor }}>
      <div ref={storyWrapperElement}>
        {isStoryPublic ? (
          <PublicStory
            story={story}
            config={config}
            templateConfig={storyConfig}
            preview={preview}
            storyElementsConfig={storyElementsConfig}
            adComponent={adComponent || AdComponent}
            widgetComp={widgetComp || WidgetComp}
            storyBaseType={storyBaseType}
          />
        ) : (
          <PaywallStory
            {...props}
            templateConfig={storyConfig}
            preview={preview}
            storyElementsConfig={storyElementsConfig}
            adComponent={adComponent || AdComponent}
            widgetComp={widgetComp || WidgetComp}
            storyBaseType={storyBaseType}
          />
        )}
      </div>
    </div>
  );
};

StoryBase.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  preview: PropTypes.bool,
  templateConfig: PropTypes.object,
  storyBaseType: PropTypes.string,
  storyStoreField: PropTypes.string,
  storyPageType: PropTypes.string,
  adComponent: PropTypes.func,
  widgetComp: PropTypes.func
};

StoryBase.defaultProps = {
  templateConfig: {}
};

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});

export const Story = connect(mapStateToProps)(StoryBase);

export default Story;
